import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LogOut,
  RefreshCw,
  Eye,
  Users,
  MessageSquare,
  CalendarDays,
  Loader2,
  Lock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ds_admin_token";

const KPIS = [
  { key: "total_views", label: "Pages vues", icon: Eye },
  { key: "unique_visitors", label: "Visiteurs uniques", icon: Users },
  { key: "messages_count", label: "Messages reçus", icon: MessageSquare },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        email,
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess();
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Connexion impossible. Réessayez."
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen navy-texture flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
        data-testid="admin-login"
      >
        <div className="flex items-center gap-3 justify-center mb-8">
          <img src="/ds-expert-logo.png" alt="DS EXPERT" className="h-12 w-12 object-contain bg-white rounded-sm p-1" />
          <span className="font-serif-display text-2xl font-bold text-white tracking-tight">
            DS EXPERT
          </span>
        </div>
        <div className="bg-white rounded-sm p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-[#1B3A5B]">
            <Lock className="h-5 w-5 text-[#C9A15A]" />
            <h1 className="font-serif-display text-2xl font-bold">Espace administration</h1>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            Connectez-vous pour consulter les statistiques du site.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                className="w-full rounded-sm border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#1B3A5B] focus:ring-2 focus:ring-[#1B3A5B]/15"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                className="w-full rounded-sm border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#1B3A5B] focus:ring-2 focus:ring-[#1B3A5B]/15"
              />
            </div>
            {error && (
              <p className="text-sm text-red-600" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A15A] text-white hover:bg-[#B38C48] disabled:opacity-60 transition-colors duration-300 px-6 py-3 text-sm font-medium rounded-sm"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              Se connecter
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        onLogout();
        return;
      }
      setError("Impossible de charger les statistiques.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const maxPage = stats ? Math.max(1, ...stats.per_page.map((p) => p.count)) : 1;
  const maxDay = stats ? Math.max(1, ...stats.per_day.map((d) => d.views)) : 1;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="navy-texture text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/ds-expert-logo.png" alt="DS EXPERT" className="h-10 w-10 object-contain bg-white rounded-sm p-1" />
            <div>
              <div className="font-serif-display text-xl font-bold leading-none">Statistiques</div>
              <div className="text-xs text-slate-300 mt-1">Tableau de bord DS EXPERT</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 transition-colors px-4 py-2 text-sm rounded-sm"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-[#C9A15A] text-white hover:bg-[#B38C48] transition-colors px-4 py-2 text-sm rounded-sm"
            >
              <LogOut className="h-4 w-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {loading && !stats ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
          </div>
        ) : error ? (
          <p className="text-red-600" data-testid="admin-stats-error">{error}</p>
        ) : stats ? (
          <>
            <p className="text-sm text-slate-500" data-testid="admin-since">
              <CalendarDays className="inline h-4 w-4 mr-1 text-[#C9A15A]" />
              Données collectées depuis le {formatDate(stats.since)}
            </p>

            {/* KPI cards */}
            <div className="mt-6 grid gap-5 sm:grid-cols-3">
              {KPIS.map((k, i) => {
                const Icon = k.icon;
                return (
                  <motion.div
                    key={k.key}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="bg-white border border-slate-200 rounded-sm p-6"
                    data-testid={`kpi-${k.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-widest text-slate-500">{k.label}</span>
                      <span className="h-9 w-9 rounded-full bg-[#C9A15A]/12 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-[#C9A15A]" />
                      </span>
                    </div>
                    <div className="mt-4 font-serif-display text-4xl font-bold text-[#1B3A5B]">
                      {stats[k.key].toLocaleString("fr-FR")}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              {/* Per page */}
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <h2 className="font-serif-display text-xl font-bold text-[#1B3A5B]">Pages vues par page</h2>
                <div className="mt-6 space-y-4" data-testid="per-page-list">
                  {stats.per_page.length === 0 && (
                    <p className="text-sm text-slate-500">Aucune donnée pour le moment.</p>
                  )}
                  {stats.per_page.map((p) => (
                    <div key={p.path}>
                      <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="font-medium text-slate-700">{p.label}</span>
                        <span className="text-slate-500">{p.count.toLocaleString("fr-FR")}</span>
                      </div>
                      <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(p.count / maxPage) * 100}%` }}
                          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                          className="h-full bg-[#C9A15A] rounded-full"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Per day */}
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <h2 className="font-serif-display text-xl font-bold text-[#1B3A5B]">Évolution (30 derniers jours)</h2>
                <div className="mt-6 flex items-end gap-1.5 h-48" data-testid="per-day-chart">
                  {stats.per_day.length === 0 && (
                    <p className="text-sm text-slate-500">Aucune donnée pour le moment.</p>
                  )}
                  {stats.per_day.map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center justify-end group relative">
                      <div className="absolute -top-8 opacity-0 group-hover:opacity-100 transition-opacity bg-[#1B3A5B] text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                        {d.day} · {d.views} vues · {d.visitors} visit.
                      </div>
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${(d.views / maxDay) * 100}%` }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="w-full min-h-[4px] bg-[#1B3A5B] group-hover:bg-[#C9A15A] transition-colors rounded-t-sm"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-slate-400 text-center">
                  Survolez une barre pour le détail
                </div>
              </div>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(
    () => !!localStorage.getItem(TOKEN_KEY)
  );

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return authed ? (
    <Dashboard onLogout={logout} />
  ) : (
    <Login onSuccess={() => setAuthed(true)} />
  );
}
